import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { 
  toggleLoginModal, 
  loginRequest, 
  verifyOtpRequest, 
  resendOtpRequest 
} from "../store/slice/authSlice";
import { X, Loader2 } from "lucide-react";

export default function LoginModal() {
  const dispatch = useDispatch();
  
  // Select Auth State
  const { 
    showLoginModal, 
    isLoading, 
    otpSent, 
    tempUserId, 
    tempSessionId, 
    tempMobile,
    error 
  } = useSelector((state) => state.auth);
  
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0); // Resend timer
  const [validationError, setValidationError] = useState("");

  // Reset local state when modal closes or opens
  useEffect(() => {
    if (!showLoginModal) {
      setPhone("");
      setOtp("");
      setTimer(0);
    }
  }, [showLoginModal]);

  // Handle Timer for Resend
  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    }
    return () => clearInterval(interval);
  }, [timer]);

  if (!showLoginModal) return null;

  // --- Step 1: Send OTP ---
  const handleSendOtp = () => {
    if (phone.length === 10) {
      setValidationError("");
      dispatch(loginRequest({ mobile: phone, auth_by: 'phone' }));
      setTimer(30); // Start cooldown
    } else {
      setValidationError("Please enter a valid 10-digit number");
    }
  };

  // --- Step 2: Verify OTP ---
  const handleVerifyOtp = () => {
    if (otp.length >= 4) {
      setValidationError("");
      dispatch(verifyOtpRequest({
        user_id: tempUserId,
        session_id: tempSessionId,
        email_or_phone: tempMobile,
        otp: otp
      }));
    } else {
      setValidationError("Please enter a valid OTP");
    }
  };

  // --- Resend OTP ---
  const handleResend = () => {
    if (tempUserId) {
      dispatch(resendOtpRequest({ user_id: tempUserId }));
      setTimer(30);
      setOtp("");
    }
  };

  const handleClose = () => {
    dispatch(toggleLoginModal(false));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col md:flex-row min-h-[500px] relative">
        
        {/* Close Button */}
        <button 
          onClick={handleClose}
          className="absolute top-4 right-4 z-10 p-2 bg-gray-100 hover:bg-gray-200 rounded-full transition"
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        {/* LEFT SIDE: Banner */}
        <div className="w-full md:w-1/2 bg-[#5d1a0c] text-white p-10 flex flex-col justify-center relative overflow-hidden">
          <div className="absolute top-[-50px] left-[-50px] w-40 h-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute bottom-[-20px] right-[-20px] w-60 h-60 rounded-full bg-yellow-500/20 blur-3xl"></div>

          <div className="relative z-10 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
              Welcome to <br/> 
              <span className="text-yellow-400">GreetIndia9</span>
            </h1>
            <p className="text-xl font-medium text-white/90">
              Create. Share. Celebrate.
            </p>
            <p className="text-sm text-white/70 mt-2">
              India's #1 Digital Invitation & Greeting Platform.
            </p>
            
            {/* <div className="mt-10 flex gap-4 opacity-80">
               <div className="w-24 h-32 bg-white/10 rounded-lg border border-white/20 transform -rotate-6"></div>
               <div className="w-24 h-32 bg-white/10 rounded-lg border border-white/20 transform rotate-3 scale-110 z-10"></div>
               <div className="w-24 h-32 bg-white/10 rounded-lg border border-white/20 transform rotate-12"></div>
            </div> */}
          </div>
        </div>

        {/* RIGHT SIDE: Form */}
        <div className="w-full md:w-1/2 bg-white p-10 flex flex-col justify-center items-center">
          
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center justify-center gap-2">
              ✨ GreetIndia9
            </h2>
            <p className="text-sm text-gray-500 mt-1">Log in to create your masterpiece</p>
          </div>

          {/* Show Error if any */}
          {(error || validationError) && (
            <div className="mb-4 p-2 w-full max-w-xs bg-red-50 text-red-600 text-xs rounded text-center border border-red-200">
              {validationError || error}
            </div>
          )}

          {!otpSent ? (
            /* STEP 1: PHONE INPUT */
            <div className="w-full max-w-xs space-y-6">
              <div>
                <label className="block text-xs font-semibold text-gray-500 mb-1 uppercase">Mobile Number</label>
                <div className="flex border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-yellow-400 transition">
                  <div className="bg-gray-50 px-3 py-3 border-r border-gray-300 flex items-center gap-1">
                    <img src="https://flagcdn.com/w20/in.png" alt="India" className="w-5" />
                    <span className="text-sm font-medium text-gray-700">+91</span>
                  </div>
                  <input 
                    type="tel" 
                    maxLength={10}
                    value={phone}
                    onChange={(e) => { setPhone(e.target.value.replace(/\D/g,'')); setValidationError(""); }}
                    placeholder="Enter 10 digit number"
                    className="flex-1 px-3 py-3 outline-none text-gray-800 font-medium placeholder:font-normal"
                    disabled={isLoading}
                  />
                </div>
              </div>

              <button 
                onClick={handleSendOtp}
                disabled={isLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 rounded-lg shadow-md hover:shadow-lg transition-all transform active:scale-95 flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? "Sending..." : "Send OTP"}
              </button>
            </div>
          ) : (
            /* STEP 2: OTP VERIFICATION */
            <div className="w-full max-w-xs space-y-6 animate-in slide-in-from-right-10 duration-300">
              <div className="text-center">
                <p className="text-sm text-gray-600">Enter OTP sent to <span className="font-bold">+91 {phone}</span></p>
                <button onClick={() => dispatch(toggleLoginModal(false))} className="text-xs text-blue-600 hover:underline mt-1">Change Number</button>
              </div>

              <div>
                <input 
                  type="text" 
                  maxLength={6}
                  value={otp}
                  onChange={(e) => { setOtp(e.target.value.replace(/\D/g,'')); setValidationError(""); }}
                  placeholder="X X X X"
                  className="w-full text-center text-2xl tracking-widest border border-gray-300 rounded-lg px-4 py-3 outline-none focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 transition"
                  disabled={isLoading}
                />
              </div>

              <button 
                onClick={handleVerifyOtp}
                disabled={isLoading}
                className="w-full bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 rounded-lg shadow-md hover:shadow-lg transition-all flex justify-center items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isLoading && <Loader2 className="w-4 h-4 animate-spin" />}
                {isLoading ? "Verifying..." : "Verify & Login"}
              </button>

              <div className="text-center">
                {timer > 0 ? (
                  <span className="text-xs text-gray-400">Resend OTP in {timer}s</span>
                ) : (
                  <button onClick={handleResend} className="text-xs text-blue-600 font-semibold hover:underline">
                    Resend OTP
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-8 text-center text-xs text-gray-400">
            By continuing, you agree to <strong>GreetIndia9's</strong>{" "}
            <Link to="/privacy-policy" onClick={handleClose} className="underline hover:text-gray-600">Terms</Link>
            {" & "}
            <Link to="/privacy-policy" onClick={handleClose} className="underline hover:text-gray-600">Privacy Policy</Link>
          </div>

        </div>
      </div>
    </div>
  );
}